import { env } from "@/config/env";

export interface AppNotifyMessage {
  eventId?: string;
  receiverType?: "STAFF" | "CUSTOMER";
  receiverId?: number;
  eventType?:
    | "STAFF_NEW_PENDING_ORDER"
    | "CUSTOMER_ORDER_ACCEPTED"
    | "CUSTOMER_COACH_DEPARTED"
    | "CUSTOMER_COACH_ARRIVED"
    | "CUSTOMER_SERVICE_STARTED"
    | "CUSTOMER_SERVICE_COMPLETED"
    | "CUSTOMER_APPEND_REQUESTED"
    | "CUSTOMER_APPEND_PAID"
    | "CUSTOMER_ORDER_CANCELLED"
    | "PONG";
  orderId?: number;
  orderSn?: string;
  coachId?: number;
  userId?: number;
  eventTime?: number;
}

export interface AppNotifyConnection {
  close: () => void;
}

export const CUSTOMER_AUTH_CHANGED_EVENT = "athome:customer-auth-changed";
export const CUSTOMER_ORDER_NOTIFY_EVENT = "athome:customer-order-notify";

const buildNotifyWsUrl = (client: "staff" | "customer", token: string) => {
  const path = `${env.apiBaseUrl || "/api/v1/app"}/notify/ws`;
  const baseUrl = /^https?:\/\//i.test(path)
    ? path.replace(/^http/i, "ws")
    : `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}${path}`;
  const separator = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${separator}client=${encodeURIComponent(client)}&token=${encodeURIComponent(token)}`;
};

export const connectAppNotify = (
  client: "staff" | "customer",
  token: string,
  onMessage: (message: AppNotifyMessage) => void,
): AppNotifyConnection => {
  let closedByClient = false;
  let socket: WebSocket | null = null;
  let reconnectTimer: number | null = null;
  let heartbeatTimer: number | null = null;
  let reconnectCount = 0;

  const clearTimers = () => {
    if (reconnectTimer) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (heartbeatTimer) {
      window.clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };

  const scheduleReconnect = () => {
    if (closedByClient) {
      return;
    }
    const delay = Math.min(15000, 1000 * 2 ** Math.min(reconnectCount, 4));
    reconnectCount += 1;
    reconnectTimer = window.setTimeout(connect, delay);
  };

  const connect = () => {
    clearTimers();
    socket = new WebSocket(buildNotifyWsUrl(client, token));
    socket.onopen = () => {
      reconnectCount = 0;
      heartbeatTimer = window.setInterval(() => {
        if (socket?.readyState === WebSocket.OPEN) {
          socket.send("ping");
        }
      }, 30000);
    };
    socket.onmessage = (event) => {
      try {
        onMessage(JSON.parse(String(event.data)));
      } catch {
        // Ignore malformed notify frames.
      }
    };
    socket.onclose = scheduleReconnect;
    socket.onerror = () => {
      socket?.close();
    };
  };

  connect();

  return {
    close() {
      closedByClient = true;
      clearTimers();
      socket?.close();
    },
  };
};
